import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = ({ onAuthSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    handleAuthCallback();
  }, [location]);

  const handleAuthCallback = async () => {
    try {
      const pathname = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      
      if (pathname.includes('/auth/magic-link')) {
        await handleMagicLinkCallback(searchParams);
      } else if (pathname.includes('/auth/google/callback')) {
        await handleGoogleCallback(searchParams);
      } else if (pathname.includes('/auth/apple/callback')) {
        await handleAppleCallback(searchParams);
      } else {
        throw new Error('Unknown callback type');
      }
    } catch (error) {
      console.error('Auth callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Authentication failed');
      toast.error('Authentication failed', {
        description: 'Please try signing in again'
      });
    }
  };

  const handleMagicLinkCallback = async (searchParams) => {
    const token = searchParams.get('token');
    
    if (!token) {
      throw new Error('No token found in magic link');
    }

    setMessage('Verifying magic link...');

    const response = await axios.post(`${BACKEND_URL}/api/auth/v2/magic-link/verify`, {
      token
    });

    if (response.data.access_token) {
      completeAuthentication(response.data);
    } else {
      throw new Error('Invalid magic link response');
    }
  };

  const handleGoogleCallback = async (searchParams) => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (!code) {
      throw new Error('No authorization code received from Google');
    }

    setMessage('Processing Google authentication...');

    const response = await axios.post(`${BACKEND_URL}/api/auth/v2/google/callback`, {
      code,
      redirect_uri: `${window.location.origin}/auth/google/callback`,
      state
    });

    if (response.data.access_token) {
      completeAuthentication(response.data);
    } else {
      throw new Error('Invalid Google OAuth response');
    }
  };

  const handleAppleCallback = async (searchParams) => {
    const code = searchParams.get('code');
    
    if (!code) {
      throw new Error('No authorization code received from Apple');
    }

    setMessage('Processing Apple Sign-In...');

    const response = await axios.post(`${BACKEND_URL}/api/auth/v2/apple/callback`, {
      authorization_code: code,
      redirect_uri: `${window.location.origin}/auth/apple/callback`
    });

    if (response.data.access_token) {
      completeAuthentication(response.data);
    } else {
      throw new Error('Invalid Apple Sign-In response');
    }
  };

  const completeAuthentication = async (authData) => {
    // Store tokens
    localStorage.setItem('access_token', authData.access_token);
    localStorage.setItem('refresh_token', authData.refresh_token);
    
    // Set axios default header for future requests
    axios.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;
    
    setStatus('success');
    setMessage(`Welcome ${authData.user.full_name || authData.user.email}!`);
    
    toast.success('Authentication successful!', {
      description: `Signed in as ${authData.user.email}`
    });

    // Auto-attach to any pending group invites (non-blocking)
    try {
      console.log('🔗 Auto-attaching user to pending group invites...');
      await axios.post(`${BACKEND_URL}/api/groups/auto-attach`, {}, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${authData.access_token}`
        }
      });
      console.log('✅ Auto-attach completed');
    } catch (error) {
      // Non-critical error - don't fail login if this fails
      console.warn('⚠️ Auto-attach failed (non-critical):', error.response?.data?.detail || error.message);
    }

    // Call the success callback
    if (onAuthSuccess) {
      onAuthSuccess(authData.user, authData.access_token);
    }

    // Redirect priority: invite/redirect_after_login > ACH return > dashboard
    setTimeout(() => {
      const redirectAfterLogin = sessionStorage.getItem('redirect_after_login');
      const achReturn = sessionStorage.getItem('ach_return_url');
      if (redirectAfterLogin) {
        sessionStorage.removeItem('redirect_after_login');
        window.location.href = redirectAfterLogin;
      } else if (achReturn) {
        sessionStorage.removeItem('ach_return_url');
        navigate(achReturn);
      } else {
        navigate('/dashboard');
      }
    }, 1500);
  };

  const handleRetry = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-6">
          {status === 'processing' && (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-gray-900">Signing you in...</h2>
                <p className="text-gray-600">{message}</p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-gray-900">Authentication successful!</h2>
                <p className="text-gray-600">{message}</p>
                <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-gray-900">Authentication failed</h2>
                  <p className="text-gray-600">{message}</p>
                </div>
                <Button onClick={handleRetry} className="bg-blue-600 hover:bg-blue-700">
                  Try again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallback;